import { useEffect, useState } from 'react';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Button, Card, CardBody, Input, Label, Spinner } from '@fluxora/ui';

import { useEsiidLookup } from '../api/esiid.queries';
import { useEnsureSessionQuery } from '../api/serviceability.queries';
import {
  hasEsiidLetters,
  isValidEsiidDigits,
  stripEsiidInput,
} from '../schemas/esiid.schema';
import { useFunnel } from '../state/FunnelProvider';
import { ResolvedMeterPanel } from './ResolvedMeterPanel';

/**
 * "Shop by ESI ID" sub-flow (SHOP-3) — the DIRECT meter on-ramp.
 *
 * The shopper types their ERCOT ESI ID; we strip it to digits, validate the
 * 10–22 length as a UX affordance (the BFF re-validates), then run
 * `POST /bff/esiid/lookup` → an enriched `MeterDetail`. On resolve we hoist the
 * meter + its opaque `candidateId` into `FunnelProvider` so the landing page
 * renders the same `MeterSummaryCard` the ZIP on-ramp produces. `not_found`
 * offers "try ZIP instead"; every other state is handled explicitly.
 *
 * ── AC3 ────────────────────────────────────────────────────────────────────
 * A typed ESI ID is display/lookup INPUT — resolved server-side against the
 * funnel session (minted on mount, like the address flow), which returns the
 * opaque `candidateId` the browser holds. The ESI ID never becomes a redemption
 * key. // TODO(SHOP-1): production resolves meter detail from GMO/ERCOT and keys
 * the session record by candidateId rather than trusting a browser-sent ESI ID.
 */
export function EsiidSubflow(): JSX.Element {
  const { t } = useTranslation();
  const funnel = useFunnel();

  const session = useEnsureSessionQuery();
  const lookup = useEsiidLookup();

  const [raw, setRaw] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const digits = stripEsiidInput(raw);
  const canSubmit = isValidEsiidDigits(digits) && !lookup.isPending;

  const handleSubmit = (): void => {
    if (hasEsiidLetters(raw)) {
      setFormError(t('shop.landing.esiid.form.letters'));
      return;
    }
    if (!isValidEsiidDigits(digits)) {
      setFormError(t('shop.landing.esiid.form.esiid.invalid'));
      return;
    }
    setFormError(null);
    lookup.mutate({ esiid: digits });
  };

  // On a resolved meter, hoist it (+ its opaque candidateId) into the funnel
  // store so the landing page renders the shared MeterSummaryCard. Effect (not a
  // handler call) so React Query's async resolution is the trigger.
  useEffect(() => {
    if (lookup.isSuccess && lookup.data.outcome === 'resolved') {
      funnel.setResolved(lookup.data.candidateId, lookup.data.meter);
    }
    // `funnel` identity is stable per render; only fire on a new success.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookup.isSuccess, lookup.data]);

  // Session prerequisite (AC3) — same posture as the ZIP on-ramp.
  if (session.isPending) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="lg" label={t('shop.address.session.loading')} />
      </div>
    );
  }
  if (session.isError) {
    return (
      <Alert variant="danger" title={t('shop.address.session.error.title')} role="alert">
        {t('shop.address.session.error.body')}
      </Alert>
    );
  }

  // Resolved — hand the visual to the shared card (defensive fallback so the tab
  // is never blank if a parent forgot to render it).
  if (funnel.meterDetail !== undefined) {
    return (
      <ResolvedMeterPanel meterDetail={funnel.meterDetail} onStartOver={() => funnel.clearResolved()} />
    );
  }

  const notFound = lookup.isSuccess && lookup.data.outcome === 'not_found';

  return (
    <div className="flex flex-col gap-4">
      <Card variant="glass" padding="md">
        <CardBody className="flex flex-col gap-3 p-0">
          <p className="text-sm text-foreground/70">{t('shop.landing.esiid.intro')}</p>
          <div className="flex flex-col gap-1">
            <Label htmlFor="esiid-subflow-esiid">{t('shop.landing.esiid.form.label')}</Label>
            <Input
              id="esiid-subflow-esiid"
              name="esiid"
              inputMode="numeric"
              autoComplete="off"
              placeholder={t('shop.landing.esiid.form.placeholder')}
              value={raw}
              onChange={(e) => {
                setRaw(e.target.value);
                if (formError !== null) setFormError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
              aria-describedby="esiid-subflow-help"
            />
            <span id="esiid-subflow-help" className="text-xs text-foreground/60">
              {t('shop.landing.esiid.form.help')}
            </span>
            {formError !== null ? <span className="text-xs text-danger">{formError}</span> : null}
          </div>

          <Button
            variant="primary"
            shape="pill"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {lookup.isPending
              ? t('shop.landing.esiid.form.submitting')
              : t('shop.landing.esiid.form.submit')}
          </Button>
        </CardBody>
      </Card>

      {lookup.isPending ? (
        <div className="flex justify-center py-2">
          <Spinner size="md" label={t('shop.landing.meter.resolving')} />
        </div>
      ) : null}

      {lookup.isError ? (
        <Alert variant="danger" title={t('shop.landing.esiid.error.title')} role="alert">
          {t('shop.landing.esiid.error.body')}
        </Alert>
      ) : null}

      {notFound ? (
        <Alert variant="warning" title={t('shop.landing.esiid.notFound.title')} role="status">
          <div className="flex flex-col items-start gap-3">
            <span>{t('shop.landing.esiid.notFound.body')}</span>
            <Button variant="secondary" size="sm" shape="pill" onClick={() => funnel.setMethod('zip')}>
              {t('shop.landing.method.zip')}
            </Button>
          </div>
        </Alert>
      ) : null}
    </div>
  );
}
